import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  listarCarteira,
  listarCronograma,
  listarSpeds,
  obterCliente,
  obterDashboardCliente,
  type ClienteDetalhado,
  type DashboardCliente,
  type LinhaCronograma,
  type LinhaSped,
  type PapelDaCarteira,
  type ParteDaOperacao,
} from '../servicos/operacao';

/**
 * Consultas da operação.
 *
 * ## Por que estes tempos de cache
 *
 * A carteira (quem é cliente, fornecedor, assessor) muda por cadastro, não por
 * operação — refazer a lista a cada foco de janela seria gastar rede para
 * receber a mesma resposta. Cinco minutos é mais que o intervalo entre duas
 * navegações da mesma sessão.
 *
 * Cálculo e cronograma mudam com o trabalho do dia, então ficam com uma janela
 * curta: quem acabou de subir um SPED espera vê-lo ao voltar para a lista.
 */

const CARTEIRA_FRESCA_POR = 5 * 60_000;
const OPERACAO_FRESCA_POR = 30_000;

export function useCarteira(
  papel: PapelDaCarteira,
  /** Só a carteira desta empresa — o que quem não é admin vê. */
  empresa?: string,
): UseQueryResult<ParteDaOperacao[], Error> {
  return useQuery({
    // A empresa entra na chave: trocar de empresa não reaproveita a lista da outra.
    queryKey: ['carteira', papel, empresa ?? null],
    queryFn: () => listarCarteira(papel, empresa),
    staleTime: CARTEIRA_FRESCA_POR,
  });
}

/**
 * Todos os clientes ativos de Crédito ICMS, com ou sem operação de Recorrente
 * — para escolher o comprador no Pontual e o cliente no Passivos.
 *
 * A carteira (`useCarteira('clientes')`) só traz quem tem cálculo ou
 * contrato: quem é cliente só do Pontual ou do Passivos, sem nunca ter
 * mandado SPED, não apareceria para ser escolhido.
 */
export function useClientesParaEscolher(): UseQueryResult<ParteDaOperacao[], Error> {
  return useQuery({
    queryKey: ['carteira', 'clientes', null, 'todos'],
    queryFn: () => listarCarteira('clientes', undefined, { todos: true }),
    staleTime: CARTEIRA_FRESCA_POR,
  });
}

export function useCliente(clienteId: string | undefined): UseQueryResult<ClienteDetalhado, Error> {
  return useQuery({
    queryKey: ['cliente', clienteId],
    queryFn: () => obterCliente(clienteId as string),
    // Sem id não há o que buscar — e `enabled` evita a requisição com `undefined`
    // na URL, que voltaria 404 e apareceria como erro na tela.
    enabled: clienteId !== undefined,
    staleTime: CARTEIRA_FRESCA_POR,
  });
}

export function useSpeds(clienteId: string | undefined): UseQueryResult<LinhaSped[], Error> {
  return useQuery({
    queryKey: ['speds', clienteId],
    queryFn: () => listarSpeds(clienteId as string),
    enabled: clienteId !== undefined,
    staleTime: OPERACAO_FRESCA_POR,
  });
}

export function useCronograma(
  clienteId: string | undefined,
): UseQueryResult<LinhaCronograma[], Error> {
  return useQuery({
    queryKey: ['cronograma', clienteId],
    queryFn: () => listarCronograma(clienteId as string),
    enabled: clienteId !== undefined,
    staleTime: OPERACAO_FRESCA_POR,
  });
}

export function useDashboardCliente(
  clienteId: string | undefined,
  ano: number,
): UseQueryResult<DashboardCliente, Error> {
  return useQuery({
    queryKey: ['dashboard-cliente', clienteId, ano],
    queryFn: () => obterDashboardCliente(clienteId as string, ano),
    enabled: clienteId !== undefined,
    staleTime: OPERACAO_FRESCA_POR,
  });
}
